import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import CampaignIcon from "@mui/icons-material/Campaign";

import { NotificationDropdown } from "./NotificationDropdown";
import { NotificationPermissionModal } from "./NotificationPermissionModal";
import { NotificationService } from "../../services";
import { resolveNotificationPath } from "../../features/notifications/notification.utils";
import { getFcmToken, onForegroundMessage } from "../../services/firebase.service";
import {
  fetchNotifications as fetchNotificationsThunk,
  markAsRead as markAsReadThunk,
  markAllAsRead as markAllAsReadThunk,
} from "../../reducers/notification";
import type { NotificationItem } from "../../features/notifications/notification.utils";
import { FCM_DEVICE_TOKEN_LS_KEY } from "src/constants";

interface NotificationManagerProps {
  icon: string;
  onViewAll: () => void;
}

const NOTIFICATION_REFRESH_INTERVAL = 30_000;
const NOTIFICATION_PROMPT_SEEN_LS_KEY = "notification_prompt_seen";
const NOTIFICATION_TOASTED_IDS_LS_KEY = "notification-toasted-ids";
const NOTIFICATION_PROMPT_REMINDER_INTERVAL = 7 * 24 * 60 * 60 * 1000; // 7 days

const toPlainText = (value = "") =>
  String(value)
    .replace(/<[^>]*>/g, "")
    .trim();

export function NotificationManager({ icon, onViewAll }: NotificationManagerProps) {
  const navigate = useNavigate();
  const dispatch = useDispatch() as any;
  const { items: notifications, unreadCount, loading, lastFetchedAt } = useSelector((state: any) => state.notifications);

  const getPersistedToastedIds = (): Set<number> => {
    try {
      const stored = localStorage.getItem(NOTIFICATION_TOASTED_IDS_LS_KEY);
      return stored ? new Set(JSON.parse(stored)) : new Set();
    } catch {
      return new Set();
    }
  };
  const hasCompletedInitialFetchRef = useRef(false);
  const shownNotificationToastIdsRef = useRef<Set<number>>(getPersistedToastedIds());
  const persistToastedIds = () => {
    localStorage.setItem(NOTIFICATION_TOASTED_IDS_LS_KEY, JSON.stringify([...shownNotificationToastIdsRef.current]));
  };
  const fcmTokenRef = useRef<string | null>(null);
  const lastFetchedAtRef = useRef<number | null>(null);
  const broadcastCounterRef = useRef(0);

  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [permissionResolved, setPermissionResolved] = useState(false);
  const [permissionLoading, setPermissionLoading] = useState(false);

  useEffect(() => {
    lastFetchedAtRef.current = lastFetchedAt;
  }, [lastFetchedAt]);

  useEffect(() => {
    if (!("Notification" in window)) {
      setPermissionResolved(true);
      return;
    }

    if (Notification.permission === "granted") {
      setPermissionResolved(true);
      return;
    }

    const stored = localStorage.getItem(NOTIFICATION_PROMPT_SEEN_LS_KEY);

    if (stored === "granted") {
      setPermissionResolved(true);
      return;
    }

    if (stored && !isNaN(Number(stored))) {
      const dismissedAt = Number(stored);
      if (Date.now() - dismissedAt < NOTIFICATION_PROMPT_REMINDER_INTERVAL) {
        setPermissionResolved(true);
        return;
      }
      localStorage.removeItem(NOTIFICATION_PROMPT_SEEN_LS_KEY);
    }

    const timer = setTimeout(() => setShowPermissionModal(true), 2000);
    return () => clearTimeout(timer);
  }, []);

  const handlePermissionAccept = async () => {
    setPermissionLoading(true);
    let result;
    try {
      result = await Notification.requestPermission();
      if (result === "granted") {
        const token = await getFcmToken();
        if (token) {
          const storedToken = localStorage.getItem(FCM_DEVICE_TOKEN_LS_KEY);
          if (storedToken && storedToken !== token) {
            try {
              await NotificationService.unregisterDevice(storedToken);
            } catch {
              /* token may already be invalid */
            }
          }
          await NotificationService.registerDevice(token);
          localStorage.setItem(FCM_DEVICE_TOKEN_LS_KEY, token);
        }
      }
    } catch (e) {
      console.warn("[FCM] Permission accept flow failed:", e);
    }
    if (result === "granted") {
      localStorage.setItem(NOTIFICATION_PROMPT_SEEN_LS_KEY, "granted");
    } else {
      localStorage.setItem(NOTIFICATION_PROMPT_SEEN_LS_KEY, Date.now().toString());
    }
    setShowPermissionModal(false);
    setPermissionLoading(false);
    setPermissionResolved(true);
  };

  const handlePermissionDismiss = () => {
    localStorage.setItem(NOTIFICATION_PROMPT_SEEN_LS_KEY, Date.now().toString());
    setShowPermissionModal(false);
    setPermissionResolved(true);
  };

  const showNotificationToast = (item: NotificationItem) => {
    const toastId = `notification-${item.id}`;
    const bodyText = toPlainText(item.body);
    const severity = item?.severity?.toUpperCase?.() || "LOW";

    const content = (
      <div>
        <strong>{item.title}</strong>
        {bodyText && <div style={{ marginTop: 4, fontSize: "0.875rem" }}>{bodyText}</div>}
      </div>
    );

    const options = {
      toastId,
      closeOnClick: true,
      onClick: () => markOneAsRead(item),
    };

    if (severity === "CRITICAL") {
      toast.error(content, options);
    } else if (severity === "HIGH") {
      toast.warn(content, options);
    } else {
      toast.info(content, options);
    }
  };

  const showBroadcastNotification = (data: Record<string, string>) => {
    const title = data.title || "Notification";
    const body = data.body || "";
    const severity = (data.severity || "LOW").toUpperCase();
    const toastId = `broadcast-${Date.now()}-${broadcastCounterRef.current++}`;

    const content = (
      <div>
        <strong>{title}</strong>
        {body && <div style={{ marginTop: 4, fontSize: "0.875rem" }}>{body}</div>}
      </div>
    );

    const options = {
      toastId,
      autoClose: false as const,
      closeOnClick: true,
      icon: () => <CampaignIcon sx={{ color: "#1976d2" }} />,
      onClick: () => {
        const path = resolveNotificationPath(data);
        if (path) navigate(path);
      },
    };

    if (severity === "CRITICAL") {
      toast.error(content, options);
    } else if (severity === "HIGH") {
      toast.warn(content, options);
    } else {
      toast.info(content, options);
    }
  };

  const markOneAsRead = (item: NotificationItem) => {
    if (item?.isRead) return;
    dispatch((markAsReadThunk as any)(item.id));
  };

  const markAllAsRead = () => {
    dispatch((markAllAsReadThunk as any)());
  };

  const handleNotificationOpen = (item: NotificationItem) => {
    if (!item?.isRead) {
      markOneAsRead(item);
    }

    const path = resolveNotificationPath(item?.data);
    if (path) {
      navigate(path);
    }
  };

  useEffect(() => {
    if (!permissionResolved) return;

    dispatch((fetchNotificationsThunk as any)());

    let cancelled = false;
    let intervalId: ReturnType<typeof setInterval> | null = null;
    let unsubFcm: (() => void) | null = null;
    let unsubSwMessage: (() => void) | null = null;

    const startPolling = () => {
      intervalId = setInterval(() => {
        const elapsed = Date.now() - (lastFetchedAtRef.current || 0);
        if (elapsed < 5000) return;
        dispatch((fetchNotificationsThunk as any)());
      }, NOTIFICATION_REFRESH_INTERVAL);
    };

    const canUseFcm = "Notification" in window && Notification.permission === "granted";

    if (!canUseFcm) {
      startPolling();
    } else {
      (async () => {
        try {
          const token = await getFcmToken();
          if (cancelled) return;
          if (!token) {
            console.warn("[FCM] No token returned despite granted permission");
            return;
          }

          const storedToken = localStorage.getItem(FCM_DEVICE_TOKEN_LS_KEY);
          if (storedToken && storedToken !== token) {
            try {
              await NotificationService.unregisterDevice(storedToken);
            } catch {
              /* old token may already be invalid */
            }
          }
          if (!storedToken || storedToken !== token) {
            await NotificationService.registerDevice(token);
            localStorage.setItem(FCM_DEVICE_TOKEN_LS_KEY, token);
          }

          if (cancelled) return;
          fcmTokenRef.current = token;
          unsubFcm = onForegroundMessage((payload: any) => {
            if (payload?.data?.skipPersist === "true") {
              showBroadcastNotification(payload.data);
            } else {
              dispatch((fetchNotificationsThunk as any)());
            }
          });

          const handleSwMessage = (event: MessageEvent) => {
            if (event.data?.type === "NOTIFICATION_REFRESH") {
              dispatch((fetchNotificationsThunk as any)());
            }
          };
          navigator.serviceWorker?.addEventListener("message", handleSwMessage);
          unsubSwMessage = () => navigator.serviceWorker?.removeEventListener("message", handleSwMessage);
        } catch (e) {
          console.warn("[FCM] Registration/subscription failed:", e);
        }
      })();
    }

    return () => {
      cancelled = true;
      if (intervalId) clearInterval(intervalId);
      if (unsubFcm) unsubFcm();
      if (unsubSwMessage) unsubSwMessage();
    };
  }, [permissionResolved, dispatch]);

  useEffect(() => {
    if (!hasCompletedInitialFetchRef.current) {
      notifications.forEach((item: NotificationItem) => shownNotificationToastIdsRef.current.add(item.id));
      persistToastedIds();
      hasCompletedInitialFetchRef.current = true;
      return;
    }

    const newItems = notifications.filter(
      (item: NotificationItem) => !item.isRead && !shownNotificationToastIdsRef.current.has(item.id),
    );

    if (newItems.length > 0) {
      newItems.forEach((item: NotificationItem) => shownNotificationToastIdsRef.current.add(item.id));

      if (newItems.length > 3) {
        toast.info(`You have ${newItems.length} new notifications`, {
          toastId: `summary-${Date.now()}`,
          closeOnClick: true,
          onClick: () => onViewAll(),
        });
      } else {
        newItems.forEach(showNotificationToast);
      }
    }

    for (const id of shownNotificationToastIdsRef.current) {
      if (!notifications.some((n) => n.id === id)) {
        shownNotificationToastIdsRef.current.delete(id);
      }
    }
    persistToastedIds();
  }, [notifications]);

  const canRequestPermission = "Notification" in window && Notification.permission === "default";
  const isBlocked = "Notification" in window && Notification.permission === "denied";

  return (
    <>
      <NotificationPermissionModal
        open={showPermissionModal}
        onEnable={handlePermissionAccept}
        onDismiss={handlePermissionDismiss}
        loading={permissionLoading}
        isBlocked={isBlocked}
      />
      <NotificationDropdown
        icon={icon}
        title="Notifications"
        unreadCount={unreadCount}
        notifications={notifications}
        onItemClick={handleNotificationOpen}
        onMarkAllAsRead={markAllAsRead}
        onViewAll={onViewAll}
        loading={loading}
        button={undefined}
        showEnableButton={canRequestPermission}
        onEnable={handlePermissionAccept}
        showBlockedMessage={isBlocked}
      />
    </>
  );
}
